// ONNX runner for static-PLY hair: mat_mod + spec_mod + bsdf_mod on WebGPU
// (when an adapter exists), view_enc on WASM. Shares the face's JSEP runtime
// (proxy=false), serialised via ortGate.
import * as ort from 'onnxruntime-web';
import { withOrtGate } from '../lib/ortGate.js';
import { hasWebGpuAdapter } from '../lib/ortSession.js';
import { SpecViewTiler } from './renderer/specViewTiler.js';
import { isDynamicSpecGrid } from './renderer/specGrid.js';

export class OnnxRunner {
  constructor(opts) {
    this.opts = opts; // { baseUrl }
    this.matMod = null;
    this.matEp = null;
    // mat_mod on WebGPU with a gpu-buffer output → the WGSL unpack reads matUv
    // on-GPU (no ~280 MB readback). runMatMod returns the gpu-buffer when this
    // AND gpuShade are set, else it downloads matUv for the CPU unpack.
    this.matGpuOut = false;
    this.specMod = null;
    this.viewEnc = null;
    this.bsdfMod = null;
    // EP spec_mod was requested with; verify via spec_mod's ms in ?perf=1.
    this.activeEp = null;
    // GPU-resident spec_mod IO: persistent input gpu-buffer so the static f_dec
    // channels (32 of 40) upload ONCE and only the 8 view_feat channels move per
    // frame, plus a gpu-buffer output. On whenever spec_mod runs on WebGPU.
    // specStaticReady gates the per-frame partial upload.
    this.specGpuIo = false;
    // spec_mod runs on the tight active grid instead of the padded one. Set only
    // when the loaded asset accepts dynamic dims AND the gpu-resident IO path is
    // live — the crop's re-lay from mat_mod's padded output happens in the WGSL
    // unpack (matModUnpacker), which only runs on that path. See specGrid.js.
    this.specCropActive = false;
    this.specStaticReady = false;
    this._specInBuffer = null;
    this._specInTensor = null;
    this._specInDims = null;
    this._specStaticCh = 32;   // # static (f_dec) channels uploaded once
    // Previous gpushade spec_mod output tensor, disposed on the next run so ORT
    // can recycle its gpu-buffer (otherwise a fresh buffer leaks every re-shade).
    this._prevSpecOut = null;
    // Tile the 8 view_feat planes on-GPU (8-float upload) instead of re-uploading
    // the ~67 MB of tiled constants every frame. On with the resident path.
    this.specViewTile = false;
    this._viewTiler = null;
    // Keep spec_mod output on the GPU and run the fused WGSL shade (no spec_out
    // readback / CPU unpack / CPU shade). On with the resident path.
    this.gpuShade = false;
    // NeuralBSDF (bsdf_mod) on WebGPU with a resident f_dec input (uploaded once,
    // since f_dec is static after mat_mod). On whenever WebGPU is available.
    this.bsdfGpu = false;
    // gpu-buffer bsdf OUTPUT (only with gpuShade): the WGSL shade binds ORT's
    // output buffer directly, so reflectance never round-trips through the CPU.
    this.bsdfGpuOut = false;
    this._prevBsdfOut = null;
    this._bsdfFeatBuffer = null;
    this._bsdfFeatTensor = null;
    this._bsdfFeatReady = false;
  }

  async init() {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const epOverride = params.get('ep');
    const threadsParam = params.get('threads');
    // The GPU-resident pipeline (resident input + on-GPU view tiling + fused WGSL
    // shade with async readback) runs whenever spec_mod runs on WebGPU; ?ep=wasm
    // forces the full CPU-tensor path, and no adapter falls back to WASM.

    // WebGPU probe: navigator.gpu needs a secure context (localhost/https), no adapter → WASM.
    const hasGpu = typeof navigator !== 'undefined' && !!navigator.gpu;
    // Shared probe (ortSession.js) so hair + texavatars decide 'WebGPU available'
    // identically — the powerPreference tweak previously had to be made twice.
    const webgpuAvailable = hasGpu && (await hasWebGpuAdapter());
    console.info(`[hair][ort] WebGPU probe → navigator.gpu=${hasGpu} adapterAvailable=${webgpuAvailable}${!webgpuAvailable ? ' — running on WASM (CPU); open http://localhost (secure context) or enable WebGPU to use the GPU' : ''}`);

    // Shared JSEP ort.env; values match the face. proxy=false required for its gpu-buffer sessions.
    ort.env.wasm.wasmPaths = '/onnxruntime/';
    ort.env.wasm.proxy = false;
    ort.env.wasm.numThreads = 1;
    if (threadsParam !== null) {
      ort.env.wasm.numThreads = parseInt(threadsParam, 10) || 1;
    }

    // spec_mod → WebGPU when an adapter exists, else WASM. ?ep=wasm forces WASM.
    let specProviders;
    if (epOverride === 'wasm' || !webgpuAvailable) {
      specProviders = ['wasm'];
    } else {
      specProviders = ['webgpu', 'wasm'];
    }
    console.info(`[hair][ort] (shared JSEP runtime) spec_mod=${specProviders.join(',')} others=wasm proxy=${ort.env.wasm.proxy} (override ?ep=wasm)`);

    // Create through the shared gate; returns { session, ep } after retry-to-wasm fallback.
    const createWith = async (url, providers, extra = {}) => {
      const make = (provs) => withOrtGate(() => ort.InferenceSession.create(url, {
        executionProviders: provs,
        graphOptimizationLevel: 'all',
        ...extra,
      }));
      try {
        return { session: await make(providers), ep: providers[0] };
      } catch (e) {
        if (providers[0] !== 'wasm') {
          console.warn(`[hair][ort] ${url} failed to build on ${providers[0]} — falling back to wasm (CPU):`, e);
          return { session: await make(['wasm']), ep: 'wasm' };
        }
        throw e;
      }
    };

    const tag = (name) => {
      const t0 = performance.now();
      console.info(`[hair][ort] creating ${name} session...`);
      return () => console.info(
        `[hair][ort] ${name} session ready in ${(performance.now() - t0).toFixed(0)} ms`,
      );
    };

    let done = tag('mat_mod');
    // mat_mod (the 6.7 MB material U-Net) runs ONCE at load over the full padded
    // grid — a heavy single-thread WASM pass, the dominant load cost once the
    // FG-LUT bake was removed. It's a
    // Conv/ConvTranspose decoder like isaac geom/appr,
    // so it runs on WebGPU when an adapter exists (?ep=wasm forces CPU). With a
    // gpu-buffer output the ~280 MB matUv tensor stays on the GPU and the WGSL
    // unpack (matModUnpacker) fans it into the shade/spec/bsdf buffers directly —
    // no readback. runMatMod downloads it (getData) only on the CPU-unpack
    // fallback (gpuShade off). Handled outside createWith so the gpu-buffer
    // output option is applied ONLY to the webgpu attempt, not the wasm fallback.
    const matUrl = `${this.opts.baseUrl}/mat_mod.onnx`;
    const matWantGpu = !(epOverride === 'wasm' || !webgpuAvailable);
    try {
      if (!matWantGpu) throw new Error('wasm-forced');
      this.matMod = await withOrtGate(() => ort.InferenceSession.create(matUrl, {
        executionProviders: ['webgpu', 'wasm'], graphOptimizationLevel: 'all',
        preferredOutputLocation: 'gpu-buffer',
      }));
      this.matEp = 'webgpu';
      this.matGpuOut = true;
    } catch (e) {
      if (matWantGpu) console.warn('[hair][ort] mat_mod webgpu build failed — wasm (CPU):', e);
      this.matMod = await withOrtGate(() => ort.InferenceSession.create(matUrl, {
        executionProviders: ['wasm'], graphOptimizationLevel: 'all',
      }));
      this.matEp = 'wasm';
      this.matGpuOut = false;
    }
    done();
    console.info(`[hair][ort] mat_mod EP = ${this.matEp}${this.matGpuOut ? ' (gpu-buffer output)' : ''}`);

    // Build spec_mod webgpu-only so ORT throws (not silently falls to CPU) if unsupported.
    done = tag('spec_mod');
    const specUrl = `${this.opts.baseUrl}/spec_mod.onnx`;
    // fp16-internal / fp32-IO variant (same recipe as the isaac geom/appr fp16
    // assets: keep_io_types, so the persistent fp32 input buffer and the WGSL
    // shade's fp32 spec_out contract are untouched). Tried first on WebGPU;
    // packs without the file just 404 → fp32 fallback.
    const specFp16Url = `${this.opts.baseUrl}/spec_mod_fp16.onnx`;
    this.specAsset = 'fp32';
    const mkSpecUrl = (url, provs, extra = {}) => withOrtGate(() => ort.InferenceSession.create(url, {
      executionProviders: provs, graphOptimizationLevel: 'all', ...extra,
    }));
    const mkSpec = async (provs, extra = {}) => {
      if (provs[0] === 'webgpu') {
        try {
          const s = await mkSpecUrl(specFp16Url, provs, extra);
          this.specAsset = 'fp16';
          return s;
        } catch (e) {
          console.info(`[hair][ort] spec_mod_fp16 unavailable (${(e?.message || e).toString().slice(0, 120)}) — using fp32 asset`);
        }
      }
      this.specAsset = 'fp32';
      return mkSpecUrl(specUrl, provs, extra);
    };
    if (specProviders[0] === 'webgpu') {
      try {
        // All-WebGPU first (fastest if every op is supported). GPU-resident IO
        // (persistent input gpu-buffer + gpu-buffer output) so we upload f_dec
        // once and only the view channels per frame, tile the view planes on-GPU,
        // and run the fused WGSL shade. Falls back to a normal webgpu session if
        // the gpu-IO build fails.
        try {
          this.specMod = await mkSpec(['webgpu'], { preferredOutputLocation: 'gpu-buffer' });
          this.activeEp = 'webgpu';
          this.specGpuIo = true;
          this.specViewTile = true;
          this._viewTiler = new SpecViewTiler();
          this.gpuShade = true;
          console.info('[hair][ort] spec_mod GPU-RESIDENT IO enabled (gpu-buffer in/out + gpu view-tile + gpu shade)');
        } catch (e) {
          console.warn('[hair][ort] spec_mod gpu-resident build failed — using a normal webgpu session:', e);
          this.specMod = await mkSpec(['webgpu']);
          this.activeEp = 'webgpu';
        }
      } catch (e) {
        // An op isn't WebGPU-capable; record why, then partition GPU+WASM.
        this.specEpError = (e?.message || String(e)).slice(0, 200);
        console.warn('[hair][ort] spec_mod not all-WebGPU — Reason:', this.specEpError, '— trying webgpu+wasm partition');
        this.specMod = await mkSpec(['webgpu', 'wasm']);
        this.activeEp = 'webgpu+wasm';
      }
    } else {
      this.specMod = await mkSpec(['wasm']);
      this.activeEp = 'wasm';
    }
    // Crop legality is a property of the loaded asset, not a flag: only a
    // spec_mod exported with symbolic (S,N) axes (hair-texavatars ac46d55) can
    // take a grid other than the one it was traced at, so an older pack keeps the
    // padded grid instead of failing at run time. Gated on specGpuIo because the
    // re-lay from mat_mod's padded output into the tight grid lives in the WGSL
    // unpack, which only runs on that path — the CPU path stages specIn at the
    // padded layout and would hand ORT a mislabelled tensor.
    this.specCropActive = this.specGpuIo && isDynamicSpecGrid(this.specMod?.inputMetadata);
    done();
    console.info(`[hair][ort] spec_mod ACTIVE EP = ${this.activeEp} asset=${this.specAsset} (jsep/webgpu build, gpuResidentIO=${this.specGpuIo}, cropActive=${this.specCropActive})${this.specEpError ? ` (all-webgpu blocked by: ${this.specEpError})` : ''}`);

    done = tag('view_enc');
    this.viewEnc = (await createWith(`${this.opts.baseUrl}/view_enc.onnx`, ['wasm'])).session;
    done();

    done = tag('bsdf_mod');
    // NeuralBSDF on WebGPU whenever an adapter exists — it's an MLP over P
    // Gaussians, ~ms on the GPU vs hundreds of ms single-thread WASM. ?ep=wasm
    // forces CPU; createWith falls back to wasm if a bsdf op isn't WebGPU-capable.
    const bsdfWasm = epOverride === 'wasm' || !webgpuAvailable;
    // With the WGSL shade active (this.gpuShade — resolved above), keep bsdf_mod's
    // output ON the GPU: the shade pass binds it directly, eliminating the old
    // GPU→CPU getData + 1→3ch CPU broadcast + CPU→GPU re-upload round trip
    // (~1.6 MB each way). The CPU shade path still needs host data, so gated.
    const bsdfExtra = (!bsdfWasm && this.gpuShade) ? { preferredOutputLocation: 'gpu-buffer' } : {};
    const bsdfRes = await createWith(`${this.opts.baseUrl}/bsdf_mod.onnx`, bsdfWasm ? ['wasm'] : ['webgpu'], bsdfExtra);
    this.bsdfMod = bsdfRes.session;
    this.bsdfGpu = bsdfRes.ep === 'webgpu';
    this.bsdfGpuOut = this.bsdfGpu && !!bsdfExtra.preferredOutputLocation;
    done();
    console.info(`[hair][ort] bsdf_mod EP = ${bsdfRes.ep}${this.bsdfGpuOut ? ' (gpu-buffer output)' : ''}`);
  }

  // The WebGPU device ORT runs spec_mod on (for the WGSL shade pass to share).
  gpuDevice() { return ort.env.webgpu?.device ?? null; }

  // Run once. In: const_latent (1,256,8,8).
  // GPU-unpack path → { gpuBuffer, dims, ortOut } (caller unpacks then disposes
  // ortOut). CPU path → { matUv, dims:[1,35,s_pad,n_pad] }.
  async runMatMod(constLatent) {
    if (!this.matMod) throw new Error('mat_mod not initialised');
    return withOrtGate(async () => {
      const t = new ort.Tensor('float32', new Float32Array(constLatent), [1, 256, 8, 8]);
      const res = await this.matMod.run({ const_latent: t });
      const out = Object.values(res)[0];
      // Keep matUv on the GPU for the WGSL unpack when the whole GPU-resident
      // shade stack is active. Requires gpuShade (resident spec_mod input, the
      // f_dec_pad target) AND bsdfGpu (resident bsdf features target) — if bsdf
      // fell back to wasm it reads CPU f_dec, so we must take the CPU unpack.
      // Caller runs matModUnpacker, then disposes ortOut.
      if (this.matGpuOut && this.gpuShade && this.bsdfGpu && out.location === 'gpu-buffer') {
        return { gpuBuffer: out.gpuBuffer, dims: out.dims.slice(), ortOut: out };
      }
      // CPU unpack fallback: download (getData for a gpu-buffer output, else data).
      const matUv = out.location === 'gpu-buffer'
        ? new Float32Array(await out.getData())
        : new Float32Array(out.data);
      try { out.dispose?.(); } catch { /* ignore */ }
      return { matUv, dims: out.dims.slice() };
    });
  }

  // Run per frame. In: (1,c,s_pad,n_pad). Out: { specOut, dims:[1,4,s_pad,n_pad] }.
  // viewFeat (Float32Array(8)) is the only per-frame-varying input; passed so the
  // resident path can tile it on-GPU instead of re-uploading the view channels.
  async runSpecMod(specIn, sPad, nPad, cIn, viewFeat) {
    if (!this.specMod) throw new Error('spec_mod not initialised');
    if (this.specGpuIo) return this._runSpecModResident(specIn, sPad, nPad, cIn, viewFeat);
    const inCopy = new Float32Array(specIn);
    return withOrtGate(async () => {
      const t = new ort.Tensor('float32', inCopy, [1, cIn, sPad, nPad]);
      const res = await this.specMod.run({ spec_in: t });
      const out = Object.values(res)[0];
      return { specOut: new Float32Array(out.data), dims: out.dims.slice() };
    });
  }

  // Write only what changed into the persistent input buffer: the 32 f_dec
  // channels once (they're static after mat_mod), the (cIn-32) view channels
  // every frame. With on-GPU view tiling the view channels are filled from an
  // 8-float uniform instead of a ~67 MB upload. Cuts per-frame H2D from the
  // full tensor to ~1/5 (resident) or to ~32 bytes (tile).
  _uploadSpecInput(device, specIn, plane, cIn, viewFeat) {
    if (!this.specStaticReady) {
      device.queue.writeBuffer(this._specInBuffer, 0, specIn); // full tensor, once
      this.specStaticReady = true;
      return false; // this upload was the full (cold) one
    }
    if (this.specViewTile && viewFeat) {
      this._viewTiler.tile(device, this._specInBuffer, viewFeat, plane, this._specStaticCh, cIn);
      return true;
    }
    const dynOff = this._specStaticCh * plane;                  // first dynamic float
    const dynLen = (cIn - this._specStaticCh) * plane;          // view channels
    device.queue.writeBuffer(this._specInBuffer, dynOff * 4, specIn, dynOff, dynLen);
    return true; // partial upload
  }

  // Create the persistent spec_mod input gpu-buffer + its ORT tensor once (also
  // required by graph capture — ORT forbids swapping the external buffer).
  _ensureSpecInBuffer(device, dims) {
    const key = dims.join(',');
    if (!this._specInBuffer) {
      const [, cIn, sPad, nPad] = dims;
      const plane = sPad * nPad;
      this._specInBuffer = device.createBuffer({
        size: cIn * plane * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC,
      });
      this._specInTensor = ort.Tensor.fromGpuBuffer(this._specInBuffer, { dataType: 'float32', dims });
      this._specInDims = key;
      this.specStaticReady = false;
    } else if (this._specInDims !== key) {
      throw new Error(`spec_mod input shape changed (${this._specInDims} → ${key})`);
    }
    return this._specInBuffer;
  }

  // Load-time hooks for the WGSL mat_mod unpack (matModUnpacker) — pre-create the
  // resident spec_mod input + bsdf feature buffers so the unpack can write the
  // f_dec channels straight into them (no CPU staging), then mark them resident
  // so the first spec_mod / bsdf run skips its cold upload.
  ensureSpecInputBuffer(cIn, sPad, nPad) {
    const device = ort.env.webgpu?.device;
    if (!device) throw new Error('webgpu device unavailable for spec_mod input buffer');
    return this._ensureSpecInBuffer(device, [1, cIn, sPad, nPad]);
  }

  markSpecStaticReady() { this.specStaticReady = true; }

  ensureBsdfFeatBuffer(N) {
    const device = ort.env.webgpu?.device;
    if (!device) throw new Error('webgpu device unavailable for bsdf feature buffer');
    if (!this._bsdfFeatBuffer) {
      this._bsdfFeatBuffer = device.createBuffer({
        size: N * 32 * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC,
      });
      this._bsdfFeatTensor = ort.Tensor.fromGpuBuffer(this._bsdfFeatBuffer, { dataType: 'float32', dims: [N, 32] });
      this._bsdfFeatReady = false;
    }
    return this._bsdfFeatBuffer;
  }

  markBsdfFeatReady() { this._bsdfFeatReady = true; }

  // GPU-resident variant: reuse one persistent gpu-buffer input (also required by
  // graph capture — ORT forbids swapping the external buffer) with partial uploads,
  // and keep the output on the GPU, reading it back per frame for the CPU unpack.
  async _runSpecModResident(specIn, sPad, nPad, cIn, viewFeat) {
    return withOrtGate(async () => {
      const device = ort.env.webgpu?.device;
      if (!device) throw new Error('webgpu device unavailable for gpu-resident spec_mod');
      const dims = [1, cIn, sPad, nPad];
      const plane = sPad * nPad;
      this._ensureSpecInBuffer(device, dims);
      this._uploadSpecInput(device, specIn, plane, cIn, viewFeat);
      const res = await this.specMod.run({ spec_in: this._specInTensor });
      const out = Object.values(res)[0];
      // gpushade: leave the output on the GPU for the WGSL shade pass. Dispose
      // the PREVIOUS frame's output (its shade has long completed) so ORT can
      // recycle the buffer instead of leaking a new one every re-shade.
      if (this.gpuShade) {
        try { this._prevSpecOut?.dispose?.(); } catch { /* ignore */ }
        this._prevSpecOut = out;
        return { specOut: null, dims: out.dims.slice(), gpuBuffer: out.gpuBuffer };
      }
      const specOut = await out.getData(); // gpu → cpu download for the unpack loop
      try { out.dispose?.(); } catch { /* ignore */ } // free the gpu-buffer output
      return { specOut, dims: out.dims.slice() };
    });
  }

  // In: (1,3) view direction. Out: Float32Array(8).
  async runViewEnc(viewDir3) {
    if (!this.viewEnc) throw new Error('view_enc not initialised');
    return withOrtGate(async () => {
      const t = new ort.Tensor('float32', new Float32Array(viewDir3), [1, 3]);
      const res = await this.viewEnc.run({ view_dir: t });
      const out = Object.values(res)[0];
      return new Float32Array(out.data);
    });
  }

  // In: sh_coeff[48], features[N*32]. Out: { reflectance[N*outCh], outCh } (outCh=1, broadcast to 3 by caller).
  async runNeuralBsdf(shCoeff48, features) {
    if (!this.bsdfMod) throw new Error('bsdf_mod not initialised');
    const N = features.length / 32;
    return withOrtGate(async () => {
      const sh = new ort.Tensor('float32', new Float32Array(shCoeff48), [1, 48]);
      // On WebGPU, keep f_dec resident: it's static after mat_mod, so upload it
      // once to a persistent gpu-buffer and reuse the tensor every (rare) re-run
      // instead of re-uploading ~N*32 floats. sh_coeff is tiny → stays a CPU tensor.
      let feat;
      if (this.bsdfGpu) {
        const device = ort.env.webgpu?.device;
        if (device) {
          if (!this._bsdfFeatBuffer) {
            this._bsdfFeatBuffer = device.createBuffer({
              size: N * 32 * 4,
              usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC,
            });
            this._bsdfFeatTensor = ort.Tensor.fromGpuBuffer(this._bsdfFeatBuffer, { dataType: 'float32', dims: [N, 32] });
            this._bsdfFeatReady = false;
          }
          if (!this._bsdfFeatReady) {
            device.queue.writeBuffer(this._bsdfFeatBuffer, 0, features instanceof Float32Array ? features : new Float32Array(features));
            this._bsdfFeatReady = true;
          }
          feat = this._bsdfFeatTensor;
        }
      }
      if (!feat) feat = new ort.Tensor('float32', new Float32Array(features), [N, 32]);
      const res = await this.bsdfMod.run({ sh_coeff: sh, features: feat });
      const out = Object.values(res)[0];
      if (this.bsdfGpuOut && out.location === 'gpu-buffer') {
        // Leave the reflectance ON the GPU — the WGSL shade binds it directly.
        // Dispose the PREVIOUS run's output now (the shade rebinds this frame);
        // the current one must stay alive until the next bsdf run, since the
        // shade's bind group references it every re-shade in between.
        try { sh.dispose?.(); } catch { /* ignore */ }
        try { this._prevBsdfOut?.dispose?.(); } catch { /* ignore */ }
        this._prevBsdfOut = out;
        return { gpuBuffer: out.gpuBuffer, outCh: out.dims[1] };
      }
      const result = { reflectance: new Float32Array(out.data), outCh: out.dims[1] };
      try { sh.dispose?.(); out.dispose?.(); } catch { /* ignore */ }
      return result;
    });
  }

  dispose() {
    // Release sessions only after the GPU queue drains: with kernel profiling
    // active (?perf=1 installs a global profiling.mode), releasing a session
    // while its timestamp queries are still in flight makes ORT's async
    // query-resolve read a released kernel — internal TypeError "reading
    // 'kernelType'" in ort.bundle's WebGpuBackend. mapAsync fences submitted
    // before this drain resolve first (queue order), so the deferred release
    // can't race them. (Hit by the StrictMode orphan runner, which disposes
    // right after its warmup shades.)
    const sessions = [this.matMod, this.specMod, this.viewEnc, this.bsdfMod];
    const release = () => {
      for (const s of sessions) { try { s?.release?.(); } catch { /* ignore */ } }
    };
    const device = ort.env.webgpu?.device;
    if (device?.queue?.onSubmittedWorkDone) device.queue.onSubmittedWorkDone().then(release, release);
    else release();
    try { this._prevSpecOut?.dispose?.(); } catch {}
    this._prevSpecOut = null;
    try { this._prevBsdfOut?.dispose?.(); } catch {}
    this._prevBsdfOut = null;
    try { this._specInBuffer?.destroy?.(); } catch {}
    try { this._bsdfFeatBuffer?.destroy?.(); } catch {}
    this._bsdfFeatBuffer = this._bsdfFeatTensor = null; this._bsdfFeatReady = false;
    try { this._viewTiler?.dispose?.(); } catch {}
    this._specInBuffer = this._specInTensor = this._specInDims = this._viewTiler = null;
    this.specStaticReady = false;
    this.matMod = this.specMod = this.viewEnc = this.bsdfMod = null;
  }
}
